import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import BaseOverviewStore, {
    type ApiResponse,
    type BaseStoreState,
} from '../../../shared/stores/baseOverviewStore';

interface ProductStoreState extends BaseStoreState {
    filterCategories: Array<{ id: number; name: string }>;
}

/**
 * Load all products and make it possible to manage them
 */
class ProductOverviewStore extends BaseOverviewStore<any> {
    protected storageKey(): string {
        return 'product';
    }

    // Implement required abstract methods
    protected defaultSortField(): string {
        return 'article_number';
    }

    protected getApiEndpoint(): string {
        return 'product';
    }

    protected getApiPath(): string {
        return 'query';
    }

    // Product-specific properties
    public available_categories: Array<{ id: number; name: string }> = [];
    public filterCategories: Array<{ id: number; name: string }> = [];
    public productDeleteConfirmation: any = null;
    public s3url: string = '';

    constructor(sdk: any) {
        super(sdk);

        // Make product-specific properties observable
        makeObservable(this, {
            available_categories: observable,
            filterCategories: observable,
            productDeleteConfirmation: observable,
            s3url: observable,
            onDeleteProduct: action,
            onDeleteProductCancel: action,
            onDeleteProductConfirm: action,
            removeFilterCategory: action,
            removeAllFilterCategories: action,
            applyFilterCategory: action,
            fetchAvailableCategories: action,
            allProducts: computed,
        });

        // Re-load from storage now that observables are set up
        this.loadFromLocalStorage();
    }

    protected buildApiParams(page: number): any {
        return {
            sortvalue: this.sortField,
            sort: this.sortOrder === 1 ? 'asc' : 'desc',
            search: this.searchValue || '',
            page,
            ...(this.filterCategories.length > 0
                ? {
                      filter: {
                          type: 'categories',
                          valueArray: this.filterCategories.map((cat: any) => cat.id),
                      },
                  }
                : {}),
        };
    }

    protected processApiResponse(response: ApiResponse): any[] {
        runInAction(() => {
            this.s3url = response.json?.s3url || '';
        });
        return response.json.data;
    }

    protected getStorageState(): ProductStoreState {
        return {
            sortOrder: this.sortOrder,
            sortField: this.sortField,
            searchValue: this.searchValue,
            filterCategories: this.filterCategories,
        };
    }

    protected applyStorageState(state: ProductStoreState): void {
        runInAction(() => {
            this.sortOrder = state.sortOrder || 1;
            this.sortField = state.sortField || this.defaultSortField();
            this.searchValue = state.searchValue || '';
            this.filterCategories = state.filterCategories || [];
        });
    }

    // Product-specific methods
    public applyFilterCategory(category: any): void {
        this.filterCategories = category;
        this.fetchList();
    }

    public removeAllFilterCategories(): void {
        this.filterCategories = [];
        this.saveToLocalStorage();
        this.fetchList();
    }

    public removeFilterCategory(category: any): void {
        this.filterCategories = this.filterCategories.filter((cat: any) => cat.id !== category.id);
        this.saveToLocalStorage();
        this.fetchList();
    }

    public onDeleteProduct(product: any): void {
        this.productDeleteConfirmation = product;
    }

    public onDeleteProductCancel(): void {
        this.productDeleteConfirmation = null;
    }

    public async onDeleteProductConfirm(): Promise<void> {
        if (this.productDeleteConfirmation?.id) {
            const result = await this.sdk.fetchApi('product', 'delete', {
                product_id: this.productDeleteConfirmation.id,
            });
            if (result?.status === 200) {
                this.fetchList();
            }
        }
        this.productDeleteConfirmation = null;
    }

    public async fetchAvailableCategories(force: boolean = false): Promise<void> {
        if (!force && this.available_categories.length > 1) {
            return;
        }
        const categoryResponse: any = await this.sdk.fetchApi('category', 'query', {});

        runInAction(() => {
            if (categoryResponse?.status === 200) {
                this.available_categories = categoryResponse.json.categories.map((cat: any) => ({
                    id: cat.id,
                    name: cat.name,
                }));
            }
        });
    }

    public async onDialogLeave(dialog: string, event: any = null): Promise<void> {
        if (dialog === 'product' && event?.event === 'SAVED') {
            await this.fetchList(this.latestPage);
        } else {
            await super.onDialogLeave(dialog, event);
        }
    }

    // Computed properties and aliases for compatibility
    get allProducts(): Array<any> | null {
        if (!this.list) return null;
        return this.list.map((item: any) => ({
            ...item,
            image: item.image ? `${this.s3url}${item.image}.jpeg` : null,
        }));
    }

    get productList(): Array<any> {
        return this.list;
    }

    get productListLoading(): boolean {
        return this.listLoading;
    }

    public fetchProductList(page: number = 1): Promise<void> {
        return this.fetchList(page);
    }
}

export default ProductOverviewStore;
